import { useState, useEffect, forwardRef } from "react";
import { X, Download, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePWA } from "@/hooks/usePWA";

export const InstallPrompt = forwardRef<HTMLDivElement>(function InstallPrompt(_, ref) {
  const { isInstallable, isInstalled, installApp, dismissInstallPrompt } = usePWA();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already installed or dismissed
    if (isInstalled || dismissed) return;

    // Check if user has dismissed before (stored in localStorage)
    const lastDismissed = localStorage.getItem("pwa-install-dismissed");
    if (lastDismissed) {
      const dismissedTime = parseInt(lastDismissed, 10);
      // Don't show again for 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Show prompt after a delay (let user explore first)
    const timer = setTimeout(() => {
      if (isInstallable) {
        setShowPrompt(true);
      }
    }, 30000); // 30 seconds delay

    return () => clearTimeout(timer);
  }, [isInstallable, isInstalled, dismissed]);

  // Also show immediately if installable and prompt becomes available later
  useEffect(() => {
    if (isInstallable && !dismissed && !isInstalled) {
      const lastDismissed = localStorage.getItem("pwa-install-dismissed");
      if (!lastDismissed) {
        const timer = setTimeout(() => setShowPrompt(true), 30000);
        return () => clearTimeout(timer);
      }
    }
  }, [isInstallable, dismissed, isInstalled]);

  const handleInstall = async () => {
    const success = await installApp();
    if (success) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    dismissInstallPrompt();
    localStorage.setItem("pwa-install-dismissed", Date.now().toString());
  };

  if (!showPrompt || !isInstallable || isInstalled) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-fade-in">
      <div className="bg-card border border-border rounded-xl shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
            <Smartphone className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground">Install Financially Fit</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Add to your home screen for quick access and offline support.
            </p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={handleInstall} className="gap-1.5">
                <Download className="w-4 h-4" />
                Install
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDismiss}>
                Maybe Later
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 hover:bg-muted rounded-md transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </div>
    </div>
  );
});
